import { Injectable, NgZone, Signal, signal, WritableSignal } from '@angular/core';
import { UserDataService } from './user-data.service';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { ProjectModel } from '@project/model/project.model';
import { RouteNavigationService } from '@services/route-navigation.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UserComponentPresenter } from '../components/user-component.presenter';
import { UserProfileVO, UserVO } from '../model/vo/user.vo';
import { Log } from '@shared/debug/log';
import { LoginInfo } from '../model/login-info';
import { UserModel } from '../model/user.model';
import { StringUtils } from '@shared/util/string-utils';
import { AlertButton } from '@shared/alert/alertButton';
import { UserProfileService } from './user-profile.service';
import { UserModelService } from '../model/user.model.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { ToastrService } from 'ngx-toastr';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { BehaviorSubject, lastValueFrom, Observable, of, Subject } from 'rxjs';
import { InitManager } from '@shared/manager/init.manager';
import { SystemConfigurationVO } from '@shared/system-configuration/system-configuration.vo';
import { QueryUtils } from '@shared/util/query.utils';
import { switchMap } from 'rxjs/operators';
import { DeactivateProjectChangeActionService } from '@shared/service/deactivate-project-change-action.service';

@Injectable({providedIn: 'root'})
export class UserSessionService {

  public static cdcEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // if Customer Data Cloud (=CDC) is enabled (in backend)
  public static cdcInitialized: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false); // if CDC has been initialized (e.g. loaded) in client
  private static _cdcInitializationStarted: boolean = false; // if CDC has already been requested/started in client

  public isLoginOutInProgress: boolean = false;
  public isNavigateToLoginPageProcess: boolean = false;

  public _isCdcAvailable: WritableSignal<boolean> = signal<boolean>(true);
  public get isCdcAvailable(): Signal<boolean> {
    return this._isCdcAvailable;
  }

  private _previousLoggedInUserProfile: UserProfileVO | undefined = undefined;

  private initConstructionSubject: Subject<void> = new Subject<void>();

  public get initConstruction$(): Observable<void> {
    return this.initConstructionSubject.asObservable();
  }

  constructor(
    private userModel: UserModel,
    private userDataService: UserDataService,
    private wagoApiClient: WagoApiClient,
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private routeNavigationService: RouteNavigationService,
    private alertHelper: AlertHelper,
    private _userComponentPresenter: UserComponentPresenter,
    private userProfileService: UserProfileService,
    private userModelService: UserModelService,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private toastrService: ToastrService,
    private localeResourceManager: LocaleResourceManager,
    private ngZone: NgZone,
    private deactivateProjectChangeActionService: DeactivateProjectChangeActionService,
  ) {
    this.subscribeOnUserForwarding();
  }

  private subscribeOnUserForwarding(): void {
    this.wagoApiClient.askUserToForwardWithLogin.subscribe({
      next: (askToElevateUserFromGuestByLoggingIn) => this.validateCurrentUserSession(askToElevateUserFromGuestByLoggingIn),
      error: (error) => console.error(`Error in askToElevateUserFromGuestByLoggingIn subscription: `, error)
    });
  }

  private async validateCurrentUserSession(askToElevateUserFromGuestByLoggingIn: boolean): Promise<void> {
    // make request to server to get user profile associated with current session (this fails of course in case session is no longer valid)
    let userProfile: UserProfileVO | undefined;
    try {
      const userIsCdcUser = await lastValueFrom(this.userDataService.userIsCdcUser());
      userProfile = await lastValueFrom(this.getCurrentUser(userIsCdcUser));
    } catch (error) {
      Log.error('Could not load current user: ', error);
      await this.continueToValidateCurrentUserSessionForNotExistingUserProfile(askToElevateUserFromGuestByLoggingIn);
      return;
    }

    if (userProfile) {
      // A user profile was returned from server and thus the user session is (still) valid.
      if (this.userModel.isGuestUser(userProfile) && askToElevateUserFromGuestByLoggingIn) {
        // If user associated with current session is a guest, then login dialog is shown in case user should elevate his rights by logging in.
        await this.promptUserToLogin(false);
      } else {
        // If the user is not a guest or we should not try to elevate rights of a guest, we continue processing (queued) requests by activating httpService again.
        // The requests, which were already executed and failed, are not tried to be executed again in such case. Instead we trigger error handling of such failed requests.
        // We will show error messages for failed requests (including generic error messages from basic error handling).
        // But for all other queued requests we will not show any further (duplicated) generic error messages.
        // We also disable any further unneccessary validations of current user session.
        this.loginActionSuccess(userProfile); // existing (CDC) session could be resumed and thus we need to notify client about newly logged in user
        this.wagoApiClient.activateAndExecuteQueuedRequests(false, true, true, true, false);
      }
    } else {
      await this.continueToValidateCurrentUserSessionForNotExistingUserProfile(askToElevateUserFromGuestByLoggingIn);
    }
  }

  private async continueToValidateCurrentUserSessionForNotExistingUserProfile(askToElevateUserFromGuestByLoggingIn: boolean): Promise<void> {
    if (this.userModel.isGuestUser(this.userModel.userProfile) && !askToElevateUserFromGuestByLoggingIn) {

      // We try to automatically re-login the guest to seamlessly resume operation
      const loginInfo: LoginInfo = UserVO.DEFAULT_GUEST_ACCOUNT_LOGIN_INFO;

      try {
        await lastValueFrom(this.userDataService.login(loginInfo));
      } catch (error) {
        Log.error('Could not load the userProfile: ', error);
        await this.promptUserToLogin(false);
        return;
      }
      this.wagoApiClient.activateAndExecuteQueuedRequests(true, false, false, false, false);
    } else {
      // In case user is not a guest or we should elevate rights from guest by logging in, we ask user to login (again)
      this.userProfileService.setUserProfile(UserProfileVO.ANONYMOUS); // we have no session or no user profile att all in this case, so we are not even a guest anymore, thus we are anonymous
      await this.promptUserToLogin(true);
    }
  }

  private async promptUserToLogin(isLogoutRequired: boolean): Promise<void> {
    this.logoutFailure(true);
    await this._userComponentPresenter.presentReloginComponent(true, false, true);
    if (this.userModel.isUserLoggedIn) {
      // User successfully logged in using the ReloginPopup
      // Now we retry to execute previously failed and queued requests. Any error resulting from executing such requests
      // will be handeled without trying to validate current user session again, as such validation has already been done:
      this.wagoApiClient.activateAndExecuteQueuedRequests(true, false, false, true, true);
    } else {
      // User has not have been logged in
      // We dont retry failed requests, as such requests would just fail again because user did not login.
      // So instead, we just show error messages for such already failed requests.
      this.wagoApiClient.activateAndExecuteQueuedRequests(false, true, true, true, true); // alternatively !isLogoutRequired could be used here to not show error messages in such case
      if (isLogoutRequired) {
        // complete logout process and put user back to landing/login page:
        await this.continueLogout(true);
      }
    }
  }

  private logoutFailure(keepGuestUserProfile: boolean = false): void {
    if (!keepGuestUserProfile && this.userModel.isGuestUser(this.userModel.userProfile)) {
      this.userProfileService.setUserProfile(UserProfileVO.ANONYMOUS);
      this.userProfileChanged(false);
    }
    this.isLoginOutInProgress = false;
  }

  private async continueLogout(navigateToLogin: boolean = false): Promise<void> {
    await this.continueLogoutProcess(navigateToLogin);
  }

  private async continueLogoutProcess(navigateToLogin: boolean = false): Promise<void> {
    if (navigateToLogin) {
      this.isNavigateToLoginPageProcess = true;
    }
    const loginInfo: LoginInfo = UserVO.DEFAULT_GUEST_ACCOUNT_LOGIN_INFO;
    await this.projectModelService.clearCurrentProject(false);
    this.isLoginOutInProgress = true;

    let userProfile: UserProfileVO;
    try {
      userProfile = await lastValueFrom(this.userDataService.login(loginInfo));
    } catch (error) {
      this.isNavigateToLoginPageProcess = false;
      this.logoutFailure();
      return;
    }

    this.isNavigateToLoginPageProcess = false;
    if (navigateToLogin) {
      try {
        this.loginActionSuccess(userProfile);
        await this.routeNavigationService.toLogin(false);
        this.isLoginOutInProgress = false;
      } catch (error) {
        Log.error('Could not navigate to login: ', error);
      }
    } else {
      this.loginActionSuccess(userProfile);
      this.isLoginOutInProgress = false;
    }
  }

  private loginActionSuccess(userProfile: UserProfileVO): void {
    this.userProfileService.setUserProfile(userProfile);
    this.userProfileChanged(true);
  }

  private userProfileChanged(success: boolean): void {
    this.loginHandler(success);
    this.userModelService.notifyLoginChanged(success);
    if (success && this.userModel.user && this.userModel.user.isRegisteredUserAccount()) {
      // message could be shown very early during startup of configurator where locales might not have yet fully loaded
      // so getValueByKeyAsObservable needs to be used because of that:
      this.localeResourceManager.getValueByKeyAsObservable('toast.message.login_successful', { 0: this.userModel.user.login })
      .subscribe((message: string) => {
        this.toastrService.info(message);
      });
    }
  }

  public async login(username?: string, password?: string, showAlertOnFailure: boolean = true): Promise<void> {
    let loginInfo: LoginInfo | undefined = undefined;
    if (!StringUtils.isEmpty(username) && !StringUtils.isEmpty(password)) {
      loginInfo = new LoginInfo(username, password, undefined);
    } else {
      loginInfo = UserVO.DEFAULT_GUEST_ACCOUNT_LOGIN_INFO;
      if (showAlertOnFailure) {
        this.showLoginAlert();
      }
    }
    await this.executeLogin(loginInfo, showAlertOnFailure);
  }

  public async cdcLogin(username: string, uid: string, signature: string, timestamp: string, showAlertOnFailure: boolean = true): Promise<void> {
    let loginInfo: LoginInfo | undefined = undefined;
    if (!StringUtils.isEmpty(username) && !StringUtils.isEmpty(uid) && !StringUtils.isEmpty(signature) && !StringUtils.isEmpty(timestamp)) {
      loginInfo = new LoginInfo(username, undefined, LoginInfo.TYPE_CDC);
      loginInfo.cdcUID = uid;
      loginInfo.cdcSignature = signature;
      loginInfo.cdcTimestamp = timestamp;
    } else {
      loginInfo = UserVO.DEFAULT_GUEST_ACCOUNT_LOGIN_INFO;
      if (showAlertOnFailure) {
        this.showLoginAlert();
      }
    }
    await this.executeLogin(loginInfo, showAlertOnFailure);
  }

  private async executeLogin(loginInfo: LoginInfo, showAlertOnFailure: boolean = true): Promise<void> {
    let userProfile: UserProfileVO;
    try {
      userProfile = await lastValueFrom(this.userDataService.login(loginInfo));
    } catch (error) {
      Log.error('Could not load user: ', error);
      // If WagoApiClient is not active, user tries to relogin, no need to clean current project until he press 'Cancel' button
      // at LoginDialog
      if (this.wagoApiClient.isActive) {
        await this.continueLogout();
      }
      if (showAlertOnFailure) {
        this.showLoginAlert();
      }
      return;
    }
    this.loginActionSuccess(userProfile);
  }

  public async reLogin(): Promise<void> {
    let userProfile: UserProfileVO | undefined;
    try {
      const userIsCdcUser = await lastValueFrom(this.userDataService.userIsCdcUser());
      userProfile = await lastValueFrom(this.getCurrentUser(userIsCdcUser));
    } catch (error) {
      // Error 404 only means that there is no user to befound, this happens on first load, before a user is set to the session and
      // can be ignored
      // @ts-ignore
      if (error?.status !== 404) {
        Log.error('Could not get current user: ', error);
      }
      await this.logout();
      return;
    }

    if (userProfile && userProfile.user) {
      this.loginActionSuccess(userProfile);
    } else {
      await this.continueLogoutProcess();
    }
  }

  public async resetAndLoginAsGuest(): Promise<void> {
    const loginInfo: LoginInfo = UserVO.DEFAULT_GUEST_ACCOUNT_LOGIN_INFO;
    await this.projectModelService.clearCurrentProject(false);

    let userProfile: UserProfileVO;
    try {
      userProfile = await lastValueFrom(this.userDataService.login(loginInfo));
    } catch (error) {
      Log.error('Login failed: ', error);
      this.isNavigateToLoginPageProcess = false;
      this.logoutFailure();
      return;
    }

    this.isNavigateToLoginPageProcess = false;
    await this.routeNavigationService.toLogin(false);
    this.loginActionSuccess(userProfile);
    this.isLoginOutInProgress = false;
  }

  public async logout(navigateToLogin: boolean = false): Promise<void> {
    if (
      !this.projectModel.currentProjectRequiresSaving ||
      !this.userModel.userProfile ||
      !this.userModel.userProfile.user ||
      !this.userModel.userProfile.user.hasPermission("SAVE_PROJECTS")
    ) {
      if (this.projectModel.currentProject) {
        await this.projectModelService.clearCurrentProject();
      }
      return this.continueLogoutProcess(navigateToLogin);
    }
    const answer = await this.alertHelper
      .showAlert('main.title.save_current_project', 'main.message.save_current_project', undefined, [AlertButton.YES, AlertButton.NO], "help");

    if (answer === AlertButton.YES) {
      await this.projectNavigationPresenter.openSaveProjectDialog();
      await this.projectModelService.clearCurrentProject();
      await this.continueLogoutProcess(navigateToLogin);
    }
    if (answer === AlertButton.NO) {
      await this.projectModelService.clearCurrentProject();
      await this.continueLogoutProcess(navigateToLogin);
    }
  }

  private showLoginAlert(): void {
    this.alertHelper.showError('serverCommunication.title.login_failed', 'serverCommunication.message.login_failed');
  }

  private getCurrentUser(userisCdcUser: boolean): Observable<UserProfileVO | undefined> {
    if (UserSessionService.cdcEnabled.getValue() && userisCdcUser) {
      return this.getLoginInfoFromCDCSession().pipe(switchMap(loginInfo => {
        if (loginInfo) {
          return this.userDataService.login(loginInfo);
        } else {
          return of(undefined)
        }
      }));
    }
    return this.getConfiguratorUser();
  }

  private getConfiguratorUser(): Observable<UserProfileVO | undefined> {
    return new Observable((subscriber) => {
      this.userDataService.getCurrentConfiguratorUser().subscribe({
        next: (userProfile: UserProfileVO) => {
          if (userProfile && userProfile.user && userProfile.user.isRegisteredUserAccount()) {
            if (userProfile.user.isProduction()) {
              this.deactivateProjectChangeActionService.addReasonToDeactivateProjectChangeAction('productionUser');
            }
            subscriber.next(userProfile);
            subscriber.complete();
          } else {
            subscriber.next(undefined);
            subscriber.complete();
          }
        },
        error: (_error: any) => {
          subscriber.next(undefined);
          subscriber.complete();
        }
      });
    });
  }

  public async initCdc(systemConfiguration: SystemConfigurationVO): Promise<void> {
    UserSessionService.cdcEnabled.next(systemConfiguration.cdcEnabled);
    await this.initCdcInternal(systemConfiguration.cdcApiDomain, systemConfiguration.cdcApiKey);
  }

  private async initCdcInternal(cdcApiDomain: string, cdcApiKey: string): Promise<void> {
    if (UserSessionService._cdcInitializationStarted || !UserSessionService.cdcEnabled.getValue()) {
      return;
    }
    UserSessionService._cdcInitializationStarted = true;
    const userSessionService: UserSessionService = this;
    (<any>window).onGigyaServiceReady = function () {
      (<any>window).gigya.accounts.addEventHandlers({
        onLogin: async (loginEvent: any) => {
          // ngZone.run needs to be done, because of external CDC event, otherwise UI might not be updated correctly (e.g. navigation to OpenProject inside login.component)
          userSessionService.ngZone.run(() => {
            if (userSessionService.userModel && !userSessionService.userModel.isRegisteredUserAccount()) {
              userSessionService.cdcLogin(loginEvent.profile.email, loginEvent.UID, loginEvent.UIDSignature, loginEvent.signatureTimestamp);
            }
          });
        },
        onLogout: (logoutEvent: any) => {
          // currently doing nothing in case CDC logut event is received
          // console.log('CDC logout event: ' + JSON.stringify(logoutEvent, null, 4));
        },
        onError: (error: any) => {
          // console.log('CDC error event: ' + JSON.stringify(error, null, 4));
        }
      });
      // ngZone.run needs to be done, because of external CDC event, otherwise UI might not be updated correctly (e.g. navigation to OpenProject inside login.component)
      userSessionService.ngZone.run(() => {
        UserSessionService.cdcInitialized.next(true);
      });
    }
    const scriptElement = document.createElement('script');;
    scriptElement.src = 'https://cdns.' + cdcApiDomain + '/js/gigya.js?apikey=' + cdcApiKey + '&no-cache=' + Date.now();
    scriptElement.onerror = () => {
      this._isCdcAvailable.set(false);
    };
    document.getElementsByTagName('head')[0].appendChild(scriptElement);
  }

  /**
   * Only performs logout, if the provided cdcUidToLogout matches the owner of the CDC session
   */
  public logoutFromCDCSession(cdcUidToLogout: string): void {
    this.getLoginInfoFromCDCSession().subscribe((loginInfo: LoginInfo | undefined) => {
      if (loginInfo && loginInfo.cdcUID === cdcUidToLogout) {
        (<any>window).gigya.accounts.logout({
          'callback': (response: any) => {
            if (response && response.errorCode === 0) {
              // console.log('CDC logout successful');
            } else {
              // console.log('CDC logout failed');
            }
          }
        });
      } else {
        // no session or cdc not enabled or cdcUidToLogout does not match owner of session
      }
    });
  }

  public getLoginInfoFromCDCSession(): Observable<LoginInfo | undefined> {
    const userSessionService: UserSessionService = this;
    const loginInfoFromCDCSession: Observable<LoginInfo | undefined> = new Observable((subscriber) => {
      InitManager.initCompleted.subscribe((initCompleted: boolean) => {
        if (initCompleted) {
          if (UserSessionService.cdcEnabled.getValue()) {
            UserSessionService.cdcInitialized.subscribe((cdcInitialized: boolean) => {
              if (cdcInitialized) {
                try {
                  (<any>window).gigya.accounts.getAccountInfo({
                    'include': 'profile', 'callback': (accountInfo: any) => {
                      userSessionService.ngZone.run(() => {
                        if (accountInfo && accountInfo.profile && !StringUtils.isEmpty(accountInfo.profile.email) && !StringUtils.isEmpty(accountInfo.UID)
                          && !StringUtils.isEmpty(accountInfo.UIDSignature) && !StringUtils.isEmpty(accountInfo.signatureTimestamp)) {
                          const loginInfo: LoginInfo = new LoginInfo(accountInfo.profile.email, undefined, LoginInfo.TYPE_CDC);
                          loginInfo.cdcUID = accountInfo.UID;
                          loginInfo.cdcSignature = accountInfo.UIDSignature;
                          loginInfo.cdcTimestamp = accountInfo.signatureTimestamp;
                          subscriber.next(loginInfo);
                          subscriber.complete();
                        } else {
                          subscriber.next(undefined);
                          subscriber.complete();
                        }
                      });
                    }
                  });
                } catch(ex) {
                  Log.error("Error while getting CDC account info: ", ex);
                  subscriber.next(undefined); // no CDC session exists
                  subscriber.complete();
                }
              }
            });
          } else {
            subscriber.next(undefined); // CDC not enabled
          }
        }
      });
    });
    return loginInfoFromCDCSession;
  }

  private loginHandler(success: boolean) {
    //ursprünglich kamm das profil mit dem message Processor
    //const userProfile: UserProfileVO = processor.data;
    const userProfile: UserProfileVO = this.userModel.userProfile;
    this.initConstruction(userProfile);
    const newUserLogin: string | undefined = userProfile && userProfile.user ? userProfile.user.login : undefined;
    if (this._previousLoggedInUserProfile && this._previousLoggedInUserProfile.user
      && this._previousLoggedInUserProfile.isRegisteredUserAccount() && this._previousLoggedInUserProfile.user.login !== newUserLogin) {
      this.logoutFromCDCSession(this._previousLoggedInUserProfile.user.login);
    }
    if (success) {
      this._previousLoggedInUserProfile = userProfile;
    }
  }

  private initConstruction(userProfile: UserProfileVO): void {
    const isUrlConstruction: string | undefined = QueryUtils.getParameterByName('construction') || '';
    this.userModel.isConstruction = StringUtils.parseBoolean(isUrlConstruction);
    if (!this.userModel.isConstruction && userProfile && userProfile.user) {
      this.userModel.isConstruction = userProfile.user.isProduction();
    }
    this.initConstructionSubject.next(undefined);
  }
}
